import { useState, useEffect, useCallback } from 'react';
import { quoteService } from '../services/quoteService';
import { apiClient } from '../services/api';
import type { Quote, QuoteFilters, QuoteDisplayInfo } from '../types/quote';
import { ApiError } from '../utils/errorHandling';

interface UseQuotesReturn {
  quotes: Quote[];
  filteredQuotes: QuoteDisplayInfo[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: QuoteFilters;
  setFilters: (filters: QuoteFilters) => void;
  viewAllOrgs: boolean;
  setViewAllOrgs: (enabled: boolean) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    totalQuotes: number;
    draftQuotes: number;
    approvedQuotes: number;
    readyForPaymentQuotes: number;
    paymentInitiatedQuotes: number;
  };
  statisticsLoading: boolean;
  refreshQuotes: () => Promise<void>;
  fetchQuotes: (page?: number, filters?: QuoteFilters, limit?: number, viewAllOrgs?: boolean) => Promise<void>;
  updateQuoteOptimistically: (id: string, updatedQuote: Partial<Quote>) => void;
  deleteQuoteOptimistically: (id: string) => void;
}

export function useQuotes(): UseQuotesReturn {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<QuoteFilters>({});
  const [viewAllOrgs, setViewAllOrgs] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [statistics, setStatistics] = useState({
    totalQuotes: 0,
    draftQuotes: 0,
    approvedQuotes: 0,
    readyForPaymentQuotes: 0,
    paymentInitiatedQuotes: 0,
  });
  const [statisticsLoading, setStatisticsLoading] = useState(true);

  // Calculate filtered quotes with display enhancements
  const filteredQuotes: QuoteDisplayInfo[] = quotes.map(quote => 
    quoteService.transformQuoteForDisplay(quote)
  );

  // Fetch statistics from backend
  const fetchStatistics = useCallback(async (viewAllOrgs: boolean = false) => {
    try {
      setStatisticsLoading(true);
      const response = await apiClient.getQuoteStatistics(viewAllOrgs);
      
      // Handle different response structures
      const statisticsData = response.data || response;
      setStatistics(statisticsData);
    } catch (error) {
      console.error('Error fetching quote statistics:', error);
      // Don't set error state for statistics - they're not critical
      // Set default statistics to prevent undefined errors
      setStatistics({
        totalQuotes: 0,
        draftQuotes: 0,
        approvedQuotes: 0,
        readyForPaymentQuotes: 0,
        paymentInitiatedQuotes: 0,
      });
    } finally {
      setStatisticsLoading(false);
    }
  }, []);

  // Fetch quotes from API
  const fetchQuotes = useCallback(async (
    page: number = 1,
    currentFilters: QuoteFilters = filters,
    limit: number = pagination.limit,
    allOrgs: boolean = viewAllOrgs
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Build filters object including search query
      const apiFilters: QuoteFilters = {
        ...currentFilters,
        ...(searchQuery && { search: searchQuery }),
      };

      const response = await quoteService.getQuotes(page, apiFilters, limit, allOrgs);
      
      setQuotes(response.quotes);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load quotes. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, pagination.limit, viewAllOrgs]);

  // Refresh quotes (reload current page with current filters)
  const refreshQuotes = useCallback(async () => {
    await Promise.all([
      fetchQuotes(pagination.page, filters, pagination.limit, viewAllOrgs),
      fetchStatistics(viewAllOrgs)
    ]);
  }, [fetchQuotes, fetchStatistics, pagination.page, filters, pagination.limit, viewAllOrgs]);

  // Optimistic update for quote modifications
  const updateQuoteOptimistically = useCallback((id: string, updatedQuote: Partial<Quote>) => {
    setQuotes(prevQuotes => 
      prevQuotes.map(quote => 
        quote.id === id ? { ...quote, ...updatedQuote } : quote
      )
    );
  }, []);

  // Optimistic delete (mark as inactive or remove from list)
  const deleteQuoteOptimistically = useCallback((id: string) => {
    setQuotes(prevQuotes => prevQuotes.filter(quote => quote.id !== id));
    
    // Update pagination totals
    setPagination(prev => ({
      ...prev,
      total: Math.max(0, prev.total - 1),
      totalPages: Math.ceil(Math.max(0, prev.total - 1) / prev.limit),
    }));
  }, []);

  // Initial load
  useEffect(() => {
    Promise.all([
      fetchQuotes(),
      fetchStatistics(viewAllOrgs)
    ]);
  }, []); // Only run on mount

  // Re-fetch when filters change
  useEffect(() => {
    if (quotes.length > 0) { // Don't run on initial mount
      Promise.all([
        fetchQuotes(1, filters, pagination.limit, viewAllOrgs),
        fetchStatistics(viewAllOrgs)
      ]);
    }
  }, [filters, viewAllOrgs]); // Don't include fetchQuotes to avoid infinite loops

  return {
    quotes,
    filteredQuotes,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    pagination,
    statistics,
    statisticsLoading,
    refreshQuotes,
    fetchQuotes,
    updateQuoteOptimistically,
    deleteQuoteOptimistically,
  };
} 